import { useMemo, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import Layout from '../../../components/layout';
import LayoutError from '../../../components/layoutError';
import NotificationModel from '../../../api/models/notification';
import DataTable from '../../../components/dataTable';
import { deleteNotification, getNotifications } from '../../../api/services/notificationService';
import { useHistory, useLocation } from 'react-router-dom';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { Notification } from '../../../components/notifications';
import District from '../../../api/models/district';
import Division from '../../../api/models/division';
import Discipline from '../../../api/models/discipline';
import Role from '../../../api/models/role';
import School from '../../../api/models/school';
import EditModal from '../../../components/editModal';
import ReactDOMServer from 'react-dom/server';
import { stripHtmlTags, sortAlphabetically } from '../../../common/utils';
import DOMPurify from 'dompurify';

type ViewState = { message?: string; error?: string; };

const deleteNotificationAsync = async (args: any) => deleteNotification(args[0]);
const fetchNotifications = async () => getNotifications();
const sanitizeInput = (input: string) => {
  return DOMPurify.sanitize(input);  
};

const ManageNotification = () => {
  const columns = useMemo(
    () => [
      {
        Header: 'Title',
        accessor: (notification: any) => (notification as NotificationModel).title.toLowerCase(),
        Cell: ({ value, column, row }: any) => sanitizeInput((row.original as NotificationModel).title),
      },
      {
        id: 'body',
        Header: 'Notification Body',
        accessor: (notification: any) => stripHtmlTags(notification.text.trim().toLowerCase()),
        Cell: ({ value, column, row }: any) => <span dangerouslySetInnerHTML={{ __html: sanitizeInput(row.original.text) }}></span>,
        width: 500,
        sortingFn: 'text',
        sortDescFirst: false
      },
      {
        Header: 'Groups',
        accessor: (n: any) => {
          {
            const notification: NotificationModel = n;
            const districts: District[] = [];
            const divisions: Division[] = [];
            const disciplines: Discipline[] = [];
            const roles: Role[] = [];
            const schools: School[] = [];

            //Process the groups
            notification.groups?.forEach(x => x.districts?.forEach(district => districts.push(district)));
            notification.groups?.forEach(group => group.divisions?.forEach(division => divisions.push(division)));
            notification.groups?.forEach(group => group.disciplines?.forEach(discipline => disciplines.push(discipline)));
            notification.groups?.forEach(group => group.roles?.forEach(role => roles.push(role)));
            notification.groups?.forEach(group => group.schools?.forEach(school => schools.push(school)));

            return sanitizeInput(stripHtmlTags(ReactDOMServer.renderToString(districts.length > 0 || divisions.length > 0 || disciplines.length > 0 || roles.length > 0 || schools.length > 0 ? <ul className="ml-5 list-disc">
              {/* Districts */}
              {districts.length > 0 ?
                <li key={`notification-${notification.notificationId}-group-districts`}>
                  District

                  <ul className="ml-4 list-disc">
                    {districts.map(district =>
                      <li key={`notification-${notification.notificationId}-group-district-${district.districtId}`}>
                        <span className="text-xs">{district.name}</span>
                      </li>
                    )}
                  </ul>
                </li> : <></>}

              {/* Divisions */}
              {divisions.length > 0 ?
                <li key={`notification-${notification.notificationId}-group-divisions`}>
                  Division

                  <ul className="ml-4 list-disc">
                    {divisions.map(division =>
                      <li key={`notification-${notification.notificationId}-group-division-${division.divisionId}`}>
                        <span className="text-xs">{division.name}</span>
                      </li>
                    )}
                  </ul>
                </li> : <></>}

              {/* Disiciplines */}
              {disciplines.length > 0 ?
                <li key={`notification-${notification.notificationId}-group-disciplines`}>
                  Disciplines

                  <ul className="ml-4 list-disc">
                    {disciplines.map(discipline =>
                      <li key={`notification-${notification.notificationId}-group-discipline-${discipline.disciplineId}`}>
                        <span className="text-xs">{discipline.name}</span>
                      </li>
                    )}
                  </ul>
                </li> : <></>}

              {/* Roles */}
              {roles.length > 0 ?
                <li key={`notification-${notification.notificationId}-group-roles`}>
                  Roles

                  <ul className="ml-4 list-disc">
                    {roles.map(role =>
                      <li key={`notification-${notification.notificationId}-group-role-${role.roleId}`}>
                        <span className="text-xs">{role.name}</span>
                      </li>
                    )}
                  </ul>
                </li> : <></>}

              {/* Schools */}
              {schools.length > 0 ?
                <li key={`notification-${notification.notificationId}-group-schools`}>
                  Schools

                  <ul className="ml-4 list-disc">
                    {schools.map(school =>
                      <li key={`notification-${notification.notificationId}-group-school-${school.schoolId}`}>
                        <span className="text-xs">{school.name}</span>
                      </li>
                    )}
                  </ul>
                </li> : <></>}
            </ul> : <>None Selected</>)).toLowerCase());
          }
        },
        Cell: ({ value, column, row }: any) => {
          const notification: NotificationModel = row.original;
          const districts: District[] = [];
          const divisions: Division[] = [];
          const disciplines: Discipline[] = [];
          const roles: Role[] = [];
          const schools: School[] = [];

          //Process the groups
          notification.groups?.forEach(x => x.districts?.forEach(district => districts.push(district)));
          notification.groups?.forEach(group => group.divisions?.forEach(division => divisions.push(division)));
          notification.groups?.forEach(group => group.disciplines?.forEach(discipline => disciplines.push(discipline)));
          notification.groups?.forEach(group => group.roles?.forEach(role => roles.push(role)));
          notification.groups?.forEach(group => group.schools?.forEach(school => schools.push(school)));

          return districts.length > 0 || divisions.length > 0 || disciplines.length > 0 || roles.length > 0 || schools.length > 0 ? <ul className="ml-5 list-disc">
            {/* Districts */}
            {districts.length > 0 ?
              <li key={`notification-${notification.notificationId}-group-districts`}>
                District

                <ul className="ml-4 list-disc">
                  {districts.map(district =>
                    <li key={`notification-${notification.notificationId}-group-district-${district.districtId}`}>
                      <span className="text-xs">{district.name}</span>
                    </li>
                  )}
                </ul>
              </li> : <></>}

            {/* Divisions */}
            {divisions.length > 0 ?
              <li key={`notification-${notification.notificationId}-group-divisions`}>
                Division

                <ul className="ml-4 list-disc">
                  {divisions.map(division =>
                    <li key={`notification-${notification.notificationId}-group-division-${division.divisionId}`}>
                      <span className="text-xs">{division.name}</span>
                    </li>
                  )}
                </ul>
              </li> : <></>}

            {/* Disiciplines */}
            {disciplines.length > 0 ?
              <li key={`notification-${notification.notificationId}-group-disciplines`}>
                Disciplines

                <ul className="ml-4 list-disc">
                  {disciplines.map(discipline =>
                    <li key={`notification-${notification.notificationId}-group-discipline-${discipline.disciplineId}`}>
                      <span className="text-xs">{discipline.name}</span>
                    </li>
                  )}
                </ul>
              </li> : <></>}

            {/* Roles */}
            {roles.length > 0 ?
              <li key={`notification-${notification.notificationId}-group-roles`}>
                Roles

                <ul className="ml-4 list-disc">
                  {roles.map(role =>
                    <li key={`notification-${notification.notificationId}-group-role-${role.roleId}`}>
                      <span className="text-xs">{role.name}</span>
                    </li>
                  )}
                </ul>
              </li> : <></>}

            {/* Schools */}
            {schools.length > 0 ?
              <li key={`notification-${notification.notificationId}-group-schools`}>
                Schools

                <ul className="ml-4 list-disc">
                  {schools.map(school =>
                    <li key={`notification-${notification.notificationId}-group-school-${school.schoolId}`}>
                      <span className="text-xs">{school.name}</span>
                    </li>
                  )}
                </ul>
              </li> : <></>}
          </ul> : <>None Selected</>;
        },
        width: 80
      },
      {
        Header: 'Action',
        accessor: (n: any) =>
          <span>
            <span className="float-left mr-2" title="Edit" onClick={() => history.push(`/admin/notifications/${n.notificationId}`)}><PencilIcon className="w-5 h-5 cursor-pointer" /></span>
            <span className="float-left" title="Delete" onClick={() => {
              setSelectedNotificationId(n.notificationId);
              setOpenDeleteModal(true);
            }}><TrashIcon className="w-5 h-5 cursor-pointer" /></span>
          </span>,
        width: 80
      },
    ],
    []
  );
  const history = useHistory();
  const { state } = useLocation<ViewState>();
  const [notifications, setNotifications] = useState<NotificationModel[]>([]);
  const [message, setMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number>(-1);

  const { error, isPending: notificationsPending } = useAsync({
    promiseFn: getNotifications,
    onResolve: (r) => setNotifications(r.data),
  });

  //Set our save or error messages if they exist
  useEffect(() => {
    if (state && state !== null && state !== undefined) {
      if (state.message && state.message !== undefined && state.message !== null && state.message !== '') {
        setMessage(state.message);
      }

      if (state.error && state.error !== undefined && state.error !== null && state.error !== '') {
        setSaveError(state.error);
      }
    }
  }, [state]);

  //Get notifications async
  const notificationsData = useAsync({
    deferFn: fetchNotifications,
    onResolve: (r) => setNotifications(r.data),
  });

  //Delete notification async
  const deleteNotification = useAsync({
    deferFn: deleteNotificationAsync,
    onResolve: (r) => {
      setMessage('The notification has been deleted');
      setSelectedNotificationId(-1);
      setOpenDeleteModal(false);
      notificationsData.run(selectedNotificationId);
    }
  })

  if (error) return <LayoutError />;

  return (
    <Layout isLoading={notificationsPending || notificationsData.isPending || deleteNotification.isPending}>
      <>
        {message && <Notification isSuccess>{message}</Notification>}
        {error && <Notification isSuccess={false}>{error}</Notification>}
        {saveError && <Notification isSuccess={false}>{saveError}</Notification>}

        <EditModal
          open={openDeleteModal}
          saveText='Delete'
          onSave={() => deleteNotification.run(selectedNotificationId)}
          onCancel={() => setOpenDeleteModal(false)}
          id={selectedNotificationId}
        ><h3>Are you sure you would like to delete this notification?</h3>
        </EditModal>

        <div className="">
          <button
            className='float-right btn btn-primary'
            onClick={() => history.push('/admin/notifications/new')}
          >New Notification</button>
        </div>
        <DataTable
          columns={columns}
          data={sortAlphabetically(notifications)}
          showPagination={true}
        />
      </>
    </Layout>
  );
};

export default ManageNotification;
